/**
 * Someone already signed in opens the global login — send them into their app.
 *
 * The address people are given is the root of the shared host, and that root
 * is this login page. Without this, reopening the app in a new tab showed the
 * sign-in form to a person whose "keep me signed in" session was still valid.
 *
 * The check is the same silent restore every app runs at boot
 * (`refreshOnLoad`): `admin` first — `createAppRoot` has already tried it by
 * the time we get here — then `mobile`, the same order and the same audience
 * re-pointing the password probe uses (`audienceProbe.ts`).
 *
 * It must never loop, and it must never hold the splash. A forward is taken at
 * most once per {@link LATCH_WINDOW_MS} in this tab, never when the visitor
 * arrived carrying a `reason` (an app sending them back on purpose), and the
 * `mobile` restore is abandoned after {@link RESTORE_DEADLINE_MS} — a refresh
 * that hangs rather than fails would otherwise leave someone staring at the
 * splash forever. Every one of those falls back to the form, which costs one
 * sign-in and cannot bounce.
 *
 * The AI connector consent page (`/connect`) is the exception. A signed-in
 * person is there to answer an AI app's request, so it never forwards; and a
 * sign-in that started there goes back there, company slug or not. A sign-in
 * that started on an AI app's approval link (`ai-actions/…` inside the app)
 * goes back to it too.
 */
import {
  configureAuthNetwork,
  refreshOnLoad,
  safeRedirectPath,
  sessionTenantSlugStore,
  accessTokenStore,
  clearProactiveRefresh,
  type Audience,
  type AuthSnapshot,
} from "@linkhr/auth";
import { authStore } from "@linkhr/auth/react";
import { landingPath } from "./audienceProbe";

/** The consent page. It needs a session, never a company. */
const CONNECT_PATH = "/connect";

const LATCH_KEY = "linkhr:entry:forwardedAt";
const LATCH_WINDOW_MS = 15_000;
/** How long the `mobile` restore may take before the form wins instead. */
const RESTORE_DEADLINE_MS = 5_000;

export interface ForwardInput {
  /** The audience a live session was found for, or `null` for none. */
  aud: Audience | null;
  /** That audience's company slug — the target app cannot boot without it. */
  slug: string | null;
  /** The loop latch: this tab forwarded moments ago, or an app sent us back. */
  held: boolean;
  /** A deep link handed to the login, kept only if it is inside the target app. */
  redirect: string | null;
}

/** Where to send a signed-in visitor, or `null` to show the form. */
export function forwardTarget({ aud, slug, held, redirect }: ForwardInput): string | null {
  if (aud === null || held) return null;
  const consent = deepLinkWithin(CONNECT_PATH, redirect);
  if (consent !== null) return consent;
  if (slug === null) return null;
  const base = landingPath(aud);
  return deepLinkWithin(base, redirect) ?? base;
}

/**
 * Where a fresh sign-in on the global login lands. A temporary password goes
 * to that app's password change, as before (a consent return is lost there);
 * otherwise back to the consent page or the AI action the sign-in started on,
 * else the app.
 */
export function landingAfterSignIn(
  aud: Audience,
  mustChangePassword: boolean,
  redirect: string | null,
): string {
  const base = landingPath(aud);
  if (mustChangePassword) return `${base}auth/change-password`;
  return (
    deepLinkWithin(CONNECT_PATH, redirect) ?? deepLinkWithin(`${base}ai-actions/`, redirect) ?? base
  );
}

/**
 * The deep link, but only when it really lands inside `base` on this origin.
 *
 * A prefix test is not enough: `/admin/%2e%2e/me/` starts with `/admin/` and
 * the browser resolves it to `/me/`, which would forward an HR admin into the
 * employee app. `URL` collapses the escapes, the dot segments and the
 * backslashes first, so what we compare is what the browser will request.
 * Still a prefix match after that, so `/connectx` passes for `/connect`:
 * harmless, as the result is always a path on this origin.
 */
export function deepLinkWithin(base: string, redirect: string | null): string | null {
  const candidate = safeRedirectPath(redirect ?? undefined);
  const origin = "https://entry.invalid";
  let url: URL;
  try {
    url = new URL(candidate, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin || !url.pathname.startsWith(base)) return null;
  return url.pathname + url.search;
}

function latchHeld(search: URLSearchParams): boolean {
  if (search.has("reason")) return true;
  try {
    const at = Number(window.sessionStorage.getItem(LATCH_KEY));
    return Number.isFinite(at) && Date.now() - at < LATCH_WINDOW_MS;
  } catch {
    // No sessionStorage, no latch — so no forward either: the form is the safe side.
    return true;
  }
}

/**
 * Give up on the restore: drop anything it left behind and put the network
 * config back where the login form expects it. A token and its refresh timer
 * belong to the app being entered, not to the form.
 */
function standDown(apiBaseUrl: string, restored: Audience | null): void {
  if (restored !== null) {
    clearProactiveRefresh();
    accessTokenStore.clear();
  }
  configureAuthNetwork({ baseUrl: apiBaseUrl, aud: "admin" });
}

/**
 * The `mobile` restore, abandoned once the deadline passes. A late answer is
 * discarded rather than awaited: by then the form is up, and the token it
 * carries is cleaned up instead of left refreshing behind the login.
 * Resolves the restored snapshot, or `null`.
 */
async function restoreMobileWithin(
  apiBaseUrl: string,
  deadlineMs: number,
): Promise<AuthSnapshot | null> {
  let abandoned = false;
  configureAuthNetwork({ baseUrl: apiBaseUrl, aud: "mobile" });
  const restore = refreshOnLoad({ aud: "mobile" }).then(
    ({ snapshot }) => {
      if (abandoned && snapshot.isAuthenticated) {
        clearProactiveRefresh();
        accessTokenStore.clear();
      }
      return snapshot.isAuthenticated ? snapshot : null;
    },
    () => null,
  );
  const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), deadlineMs));
  const restored = await Promise.race([restore, timeout]);
  if (restored === null) abandoned = true;
  return restored;
}

/**
 * `createAppRoot`'s `afterRestore`. Resolves `true` once the page is leaving.
 * Never throws: any failure shows the form, exactly as before.
 */
export async function resumeSignedInSession(
  adminSnapshot: AuthSnapshot,
  apiBaseUrl: string,
): Promise<boolean> {
  let restored: Audience | null = null;
  try {
    // The consent page never forwards. It mounts with whichever session there
    // is: `admin` is already in the store, `mobile` is put there now. Any case,
    // as the router and the Pages Function both match it.
    const path = window.location.pathname.toLowerCase();
    if (path === CONNECT_PATH || path === `${CONNECT_PATH}/`) {
      if (adminSnapshot.isAuthenticated) return false;
      const mobile = await restoreMobileWithin(apiBaseUrl, RESTORE_DEADLINE_MS);
      if (mobile === null) {
        standDown(apiBaseUrl, null);
        return false;
      }
      restored = "mobile";
      // `<AuthProvider audience>` stays "admin" after this. It only feeds login
      // request bodies, and this page sends none; the refresh path and the
      // audience header already follow the network config, now "mobile".
      authStore.set(mobile);
      return false;
    }

    const search = new URLSearchParams(window.location.search);
    if (latchHeld(search)) return false;

    let aud: Audience | null = adminSnapshot.isAuthenticated ? "admin" : null;
    if (aud === null && (await restoreMobileWithin(apiBaseUrl, RESTORE_DEADLINE_MS)) !== null) {
      aud = "mobile";
      restored = "mobile";
    }

    const target = forwardTarget({
      aud,
      slug: aud === null ? null : sessionTenantSlugStore.get(aud),
      held: false,
      redirect: search.get("redirect"),
    });
    if (target === null) {
      standDown(apiBaseUrl, restored);
      return false;
    }
    window.sessionStorage.setItem(LATCH_KEY, String(Date.now()));
    window.location.replace(target);
    return true;
  } catch {
    // Includes a refused latch write (storage full / blocked): the forward
    // never happened, so nothing may be left holding a session either.
    standDown(apiBaseUrl, restored);
    return false;
  }
}
